import { DashboardPropertyReport, DashboardPropertyReportRow } from "types";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { LoaderFunctionArgs, useLoaderData, useNavigation } from "react-router";
import { useTranslation } from "react-i18next";
import { getDashboardPropertyReport } from "api/reports";
import AuthService from "utils/AuthService";
import {
  useDefaultTableOptions,
  TableType,
} from "hooks/useDefaultTableOptions";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { MRT_ColumnDef } from "material-react-table";
interface PropertyReportLoaderData {
  _propertyReport: Promise<DashboardPropertyReport>;
}

export const propertyReportLoader = async ({
  params,
}: LoaderFunctionArgs): Promise<PropertyReportLoaderData> => {
  const userID = AuthService.GetUserID();

  const report = getDashboardPropertyReport({
    tenantID: params.tenantID,
    userID: userID,
  });
  return {
    _propertyReport: report,
  };
};

const PropertyReport = (): ReactNode => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { _propertyReport } = useLoaderData() as PropertyReportLoaderData;
  const [rows, setRows] = useState<DashboardPropertyReportRow[]>([]);

  useEffect(() => {
    const awaitData = async () => {
      const report = await _propertyReport;
      setRows(report.rows);
    };
    awaitData();
  }, [_propertyReport]);

  const columns = useMemo<MRT_ColumnDef<DashboardPropertyReportRow>[]>(
    () => [
      {
        header: t("columns.id", { ns: "reports" }),
        accessorKey: "propertyID",
        size: 50,
      },
      {
        header: t("columns.property_name", { ns: "reports" }),
        accessorKey: "propertyName",
      },
      {
        header: t("columns.completed_jobs_24", { ns: "reports" }),
        accessorKey: "completedJobs24",
        size: 50,
      },
      {
        header: t("columns.completed_jobs_week", { ns: "reports" }),
        accessorKey: "completedJobsWeek",
        size: 50,
      },
      {
        header: t("columns.completed_jobs_month", { ns: "reports" }),
        accessorKey: "completedJobsMonth",
        size: 50,
      },
      {
        header: t("columns.completed_jobs_year", { ns: "reports" }),
        accessorKey: "completedJobsYear",
        size: 50,
      },
    ],
    [],
  );

  const tableDefaults = useDefaultTableOptions<DashboardPropertyReportRow>(
    TableType.SIMPLE,
  );
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: rows,
    columns,
    initialState: {
      density: "compact",
    },
    state: {
      isLoading: navigation.state === "loading",
      showLoadingOverlay: false,
    },
    getRowId: (row) => `${row.propertyID}`,
    muiTableContainerProps: () => ({
      sx: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        maxHeight: `calc(100dvh - 100px - 70px - 32px - 182px)`,
        minHeight: `calc(100dvh - 100px - 70px - 32px - 182px)`,
      },
    }),
  });

  return <MaterialReactTable table={table} />;
};

export default PropertyReport;
